import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Landmark, Plus, Pencil, Trash2, Eye, Download, Ban, FileText, Upload, KeyRound, Users, Boxes, Receipt,
} from "lucide-react";
import api, { downloadBlob, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProjects } from "@/context/ProjectContext";
import { PageHeader, SectionCard, Loading, EmptyState, Money, StatCard } from "@/components/Common";
import { formatRupiah, formatTanggal } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const todayWIB = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Jakarta" }).format(new Date());
const TYPE_LABEL = { dp: "DP", installment: "Angsuran", settlement: "Pelunasan" };

function readFile(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve({ base64: r.result, mime: file.type });
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function RowStatus({ status, late }) {
  const map = { lunas: ["Lunas", "#047857", "#ecfdf5"], sebagian: ["Sebagian", "#c2410c", "#fff7ed"], belum: ["Belum Dibayar", "#475569", "#f1f5f9"] };
  const [t, c, bg] = map[status] || map.belum;
  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className="rounded-full px-2 py-0.5 text-xs font-medium" style={{ color: c, background: bg }}>{t}</span>
      {late && <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">Terlambat</span>}
    </div>
  );
}

export default function AngsuranInvestor() {
  const [tab, setTab] = useState("transaksi");
  const [detailId, setDetailId] = useState(null);
  return (
    <>
      <PageHeader title="Angsuran Investor" description="Pembelian unit properti, DP, angsuran, pelunasan, dan laporan penerimaan uang per proyek." />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="transaksi" data-testid="ai-tab-transaksi"><Receipt className="mr-2 h-4 w-4" />Transaksi</TabsTrigger>
          <TabsTrigger value="unit" data-testid="ai-tab-unit"><Boxes className="mr-2 h-4 w-4" />Unit/Blok</TabsTrigger>
          <TabsTrigger value="investor" data-testid="ai-tab-investor"><Users className="mr-2 h-4 w-4" />Investor</TabsTrigger>
          <TabsTrigger value="laporan" data-testid="ai-tab-laporan"><FileText className="mr-2 h-4 w-4" />Laporan</TabsTrigger>
        </TabsList>
        <TabsContent value="transaksi"><TransaksiTab onOpen={setDetailId} /></TabsContent>
        <TabsContent value="unit"><UnitTab /></TabsContent>
        <TabsContent value="investor"><InvestorTab /></TabsContent>
        <TabsContent value="laporan"><LaporanTab /></TabsContent>
      </Tabs>
      {detailId && <PurchaseDetail id={detailId} onClose={() => setDetailId(null)} />}
    </>
  );
}

/* ----------------------------- Transaksi ----------------------------- */
function TransaksiTab({ onOpen }) {
  const { projectId, projectParam } = useProjects();
  const [status, setStatus] = useState("all");
  const [create, setCreate] = useState(false);
  const { data: list = [], isLoading } = useQuery({
    queryKey: ["ai-purchases", projectId, status],
    queryFn: async () => (await api.get("/angsuran/purchases", { params: { ...projectParam, status: status === "all" ? undefined : status } })).data,
  });
  return (
    <SectionCard
      title="Transaksi Pembelian" description="Setiap transaksi terhubung ke satu proyek, satu investor, dan satu unit."
      actions={<div className="flex items-center gap-2">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-[150px]" data-testid="ai-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Status</SelectItem><SelectItem value="belum_lunas">Belum Lunas</SelectItem><SelectItem value="lunas">Lunas</SelectItem></SelectContent>
        </Select>
        <Button onClick={() => setCreate(true)} data-testid="ai-add-purchase"><Plus className="mr-2 h-4 w-4" />Tambah Transaksi</Button>
      </div>}
      className="!p-0 overflow-hidden"
    >
      {isLoading ? <Loading /> : list.length === 0 ? (
        <div className="p-6"><EmptyState title="Belum ada transaksi" description="Tambahkan transaksi pembelian unit oleh investor." action={<Button onClick={() => setCreate(true)} data-testid="ai-empty-add-purchase"><Plus className="mr-2 h-4 w-4" />Tambah Transaksi</Button>} /></div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Investor</TableHead><TableHead>Unit</TableHead><TableHead>Tgl</TableHead><TableHead className="text-right">Nilai</TableHead><TableHead className="text-right">Terbayar</TableHead><TableHead className="text-right">Sisa</TableHead><TableHead>Jatuh Tempo</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {list.map((p) => (
                <TableRow key={p.id} data-testid={`ai-purchase-row-${p.id}`}>
                  <TableCell className="font-medium">{p.buyer_name}</TableCell>
                  <TableCell>{p.unit_label}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{formatTanggal(p.transaction_date)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatRupiah(p.total_amount)}</TableCell>
                  <TableCell className="text-right tabular-nums text-emerald-700">{formatRupiah(p.net_paid)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{formatRupiah(p.remaining)}</TableCell>
                  <TableCell className="text-xs">{p.nearest_due ? formatTanggal(p.nearest_due) : "-"}{p.arrears > 0 && <span className="ml-1 text-red-600">(tunggakan)</span>}</TableCell>
                  <TableCell><Badge variant={p.status === "lunas" ? "default" : "secondary"} className={p.status === "lunas" ? "bg-emerald-600" : ""}>{p.status === "lunas" ? "Lunas" : "Belum Lunas"}</Badge></TableCell>
                  <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => onOpen(p.id)} data-testid={`ai-open-${p.id}`}>Detail</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {create && <CreatePurchaseDialog onClose={() => setCreate(false)} />}
    </SectionCard>
  );
}

function CreatePurchaseDialog({ onClose }) {
  const qc = useQueryClient();
  const { projects, projectId } = useProjects();
  const [f, setF] = useState({
    project_id: projectId !== "all" ? projectId : "", buyer_id: "", unit_id: "",
    transaction_date: todayWIB(), doc_ref: "", total_amount: "", dp_amount: "0",
    dp_due_date: "", installment_count: "", first_installment_due_date: "", note: "",
  });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const { data: buyers = [] } = useQuery({ queryKey: ["ai-buyers"], queryFn: async () => (await api.get("/angsuran/buyers")).data });
  const { data: units = [] } = useQuery({
    queryKey: ["ai-units-avail", f.project_id],
    queryFn: async () => f.project_id ? (await api.get("/angsuran/units", { params: { project_id: f.project_id, available_only: true } })).data : [],
    enabled: !!f.project_id,
  });
  const set = (k, v) => { setF((s) => ({ ...s, [k]: v })); setPreview(null); };
  const body = () => ({
    project_id: f.project_id, buyer_id: f.buyer_id, unit_id: f.unit_id, transaction_date: f.transaction_date,
    doc_ref: f.doc_ref, total_amount: Number(f.total_amount || 0), dp_amount: Number(f.dp_amount || 0),
    dp_due_date: f.dp_due_date || null, installment_count: Number(f.installment_count || 0),
    first_installment_due_date: f.first_installment_due_date || null, note: f.note,
  });
  const doPreview = async () => {
    try { setPreview((await api.post("/angsuran/purchases/preview", body())).data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const save = async () => {
    if (!f.project_id || !f.buyer_id || !f.unit_id) { toast.error("Proyek, investor, dan unit wajib dipilih."); return; }
    setLoading(true);
    try {
      await api.post("/angsuran/purchases", body());
      toast.success("Transaksi pembelian dibuat.");
      qc.invalidateQueries({ queryKey: ["ai-purchases"] });
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="ai-create-purchase-dialog">
        <DialogHeader><DialogTitle>Transaksi Pembelian Baru</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><Label>Proyek</Label>
            <Select value={f.project_id} onValueChange={(v) => { set("project_id", v); set("unit_id", ""); }}>
              <SelectTrigger data-testid="ai-p-project"><SelectValue placeholder="Pilih proyek" /></SelectTrigger>
              <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Investor / Pembeli</Label>
            <Select value={f.buyer_id} onValueChange={(v) => set("buyer_id", v)}>
              <SelectTrigger data-testid="ai-p-buyer"><SelectValue placeholder="Pilih investor" /></SelectTrigger>
              <SelectContent>{buyers.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Blok / Unit</Label>
            <Select value={f.unit_id} onValueChange={(v) => set("unit_id", v)} disabled={!f.project_id}>
              <SelectTrigger data-testid="ai-p-unit"><SelectValue placeholder={f.project_id ? "Pilih unit tersedia" : "Pilih proyek dulu"} /></SelectTrigger>
              <SelectContent>{units.map((u) => <SelectItem key={u.id} value={u.id}>{u.code}{u.name ? ` — ${u.name}` : ""}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Tanggal Transaksi</Label><Input type="date" max={todayWIB()} value={f.transaction_date} onChange={(e) => set("transaction_date", e.target.value)} data-testid="ai-p-date" /></div>
          <div><Label>No. Dokumen (opsional)</Label><Input value={f.doc_ref} onChange={(e) => set("doc_ref", e.target.value)} data-testid="ai-p-docref" /></div>
          <div><Label>Nilai Transaksi Final</Label><Input type="number" value={f.total_amount} onChange={(e) => set("total_amount", e.target.value)} data-testid="ai-p-total" />{f.total_amount && <p className="mt-0.5 text-xs text-muted-foreground">{formatRupiah(f.total_amount)}</p>}</div>
          <div><Label>DP Disepakati</Label><Input type="number" value={f.dp_amount} onChange={(e) => set("dp_amount", e.target.value)} data-testid="ai-p-dp" />{f.dp_amount && <p className="mt-0.5 text-xs text-muted-foreground">{formatRupiah(f.dp_amount)}</p>}</div>
          <div><Label>Jatuh Tempo DP</Label><Input type="date" value={f.dp_due_date} onChange={(e) => set("dp_due_date", e.target.value)} data-testid="ai-p-dpdue" /></div>
          <div><Label>Jumlah Angsuran</Label><Input type="number" value={f.installment_count} onChange={(e) => set("installment_count", e.target.value)} data-testid="ai-p-count" /></div>
          <div><Label>Jatuh Tempo Angsuran ke-1</Label><Input type="date" value={f.first_installment_due_date} onChange={(e) => set("first_installment_due_date", e.target.value)} data-testid="ai-p-firstdue" /></div>
          <div className="sm:col-span-2"><Label>Catatan</Label><Textarea value={f.note} onChange={(e) => set("note", e.target.value)} data-testid="ai-p-note" /></div>
        </div>
        <div className="mt-2">
          <Button variant="outline" onClick={doPreview} data-testid="ai-p-preview-btn"><Eye className="mr-2 h-4 w-4" />Lihat Pratinjau Jadwal</Button>
        </div>
        {preview && (
          <div className="mt-2 rounded-lg border border-border p-3" data-testid="ai-p-preview">
            <div className="mb-2 text-xs text-muted-foreground">Sisa setelah DP: <b>{formatRupiah(preview.sisa_setelah_dp)}</b> · Total jadwal: <b>{formatRupiah(preview.total_schedule)}</b></div>
            <div className="max-h-56 overflow-y-auto">
              <Table><TableHeader><TableRow><TableHead>Jenis</TableHead><TableHead>Jatuh Tempo</TableHead><TableHead className="text-right">Nominal</TableHead></TableRow></TableHeader>
                <TableBody>{preview.rows.map((r, i) => <TableRow key={i}><TableCell>{r.label}</TableCell><TableCell className="text-xs">{formatTanggal(r.due_date)}</TableCell><TableCell className="text-right tabular-nums">{formatRupiah(r.amount_billed)}</TableCell></TableRow>)}</TableBody>
              </Table>
            </div>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={loading} data-testid="ai-p-save">Simpan Transaksi</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ----------------------------- Detail + Payment ----------------------------- */
function PurchaseDetail({ id, onClose }) {
  const qc = useQueryClient();
  const { isSuperadmin } = useAuth();
  const [addPay, setAddPay] = useState(false);
  const [reverseTarget, setReverseTarget] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["ai-purchase", id], queryFn: async () => (await api.get(`/angsuran/purchases/${id}`)).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["ai-purchase", id] }); qc.invalidateQueries({ queryKey: ["ai-purchases"] }); };
  const openReceipt = async (p) => {
    try { const res = await api.get(`/angsuran/payments/${p.id}/receipt`, { responseType: "blob" }); window.open(URL.createObjectURL(res.data), "_blank"); }
    catch { toast.error("Struk tidak tersedia."); }
  };
  const dlReceipt = async (p) => {
    try { const res = await api.get(`/angsuran/payments/${p.id}/receipt`, { responseType: "blob" }); downloadBlob(res.data, `struk-${p.receipt_number || p.id}.pdf`); }
    catch { toast.error("Struk tidak tersedia."); }
  };
  const dlCard = async () => {
    try { const res = await api.get(`/angsuran/purchases/${id}/kartu.pdf`, { responseType: "blob" }); downloadBlob(res.data, `kartu-angsuran-${data?.buyer?.name || id}.pdf`); }
    catch { toast.error("Gagal mengunduh kartu angsuran."); }
  };
  const s = data?.summary;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[94vh] max-w-4xl overflow-y-auto" data-testid="ai-detail-dialog">
        <DialogHeader><DialogTitle>Detail Transaksi — {data?.buyer?.name || ""}</DialogTitle></DialogHeader>
        {isLoading || !data ? <Loading /> : (
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">
              {data.project_name} · Unit {data.unit?.code}{data.unit?.name ? ` (${data.unit.name})` : ""} · No. Transaksi {data.purchase.doc_ref || "-"}
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <StatCard label="Nilai Transaksi" value={<Money value={s.total_amount} />} testid="ai-s-total" />
              <StatCard label="Total Terbayar (bersih)" value={<Money value={s.net_paid} />} accent="#047857" testid="ai-s-paid" />
              <StatCard label="Sisa Kewajiban" value={<Money value={s.remaining} />} accent="#dd6030" testid="ai-s-remaining" />
              <StatCard label="Status" value={s.status === "lunas" ? "Lunas" : "Belum Lunas"} accent={s.status === "lunas" ? "#047857" : "#64748b"} testid="ai-s-status" />
              <StatCard label="DP Disepakati" value={<Money value={s.dp_agreed} />} />
              <StatCard label="DP Dibayar" value={<Money value={s.dp_paid} />} />
              <StatCard label="Periode Belum Lunas" value={s.unpaid_installment_count} hint="baris angsuran bersisa" />
              <StatCard label="Total Tunggakan" value={<Money value={s.arrears} />} accent="#b91c1c" hint={s.nearest_due ? `Jatuh tempo terdekat ${formatTanggal(s.nearest_due)}` : "-"} />
            </div>

            <SectionCard title="Jadwal & Tagihan" className="!p-0 overflow-hidden"
              actions={<div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={dlCard} data-testid="ai-download-card"><Download className="mr-2 h-4 w-4" />Kartu Angsuran</Button>
                {s.remaining > 0 && <Button size="sm" onClick={() => setAddPay(true)} data-testid="ai-add-payment"><Plus className="mr-2 h-4 w-4" />Tambah Pembayaran</Button>}
              </div>}>
              <div className="overflow-x-auto">
                <Table><TableHeader><TableRow><TableHead>Jenis</TableHead><TableHead>Jatuh Tempo</TableHead><TableHead className="text-right">Tagihan</TableHead><TableHead className="text-right">Terbayar</TableHead><TableHead className="text-right">Sisa</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                  <TableBody>{data.rows.map((r) => (
                    <TableRow key={r.id} data-testid={`ai-row-${r.id}`}>
                      <TableCell className="font-medium">{r.label}</TableCell>
                      <TableCell className="text-xs">{formatTanggal(r.due_date)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatRupiah(r.amount_billed)}</TableCell>
                      <TableCell className="text-right tabular-nums text-emerald-700">{formatRupiah(r.paid)}</TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">{formatRupiah(r.remaining)}</TableCell>
                      <TableCell><RowStatus status={r.status} late={r.late} /></TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              </div>
            </SectionCard>

            <SectionCard title="Riwayat Pembayaran" className="!p-0 overflow-hidden">
              {data.payments.length === 0 ? <div className="p-5 text-sm text-muted-foreground">Belum ada pembayaran.</div> : (
                <div className="overflow-x-auto">
                  <Table><TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>Jenis</TableHead><TableHead className="text-right">Nominal</TableHead><TableHead>Metode</TableHead><TableHead>No. Struk</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
                    <TableBody>{data.payments.map((p) => (
                      <TableRow key={p.id} data-testid={`ai-pay-${p.id}`} className={p.status === "reversed" ? "opacity-60" : ""}>
                        <TableCell className="text-xs">{formatTanggal(p.received_at)}</TableCell>
                        <TableCell>{TYPE_LABEL[p.payment_type] || p.payment_type}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatRupiah(p.amount)}</TableCell>
                        <TableCell className="text-xs">{p.method === "transfer" ? "Transfer" : "Tunai"}</TableCell>
                        <TableCell className="text-xs">{p.receipt_number || "-"}</TableCell>
                        <TableCell>{p.status === "reversed" ? <Badge variant="secondary" className="bg-red-100 text-red-700">Dibatalkan</Badge> : <Badge className="bg-emerald-600">Aktif</Badge>}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="icon" variant="ghost" className="h-8 w-8" title="Lihat struk" onClick={() => openReceipt(p)} data-testid={`ai-receipt-view-${p.id}`}><Eye className="h-4 w-4" /></Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8" title="Unduh struk" onClick={() => dlReceipt(p)} data-testid={`ai-receipt-dl-${p.id}`}><Download className="h-4 w-4" /></Button>
                            {isSuperadmin && p.status === "active" && <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" title="Batalkan (reversal)" onClick={() => setReverseTarget(p)} data-testid={`ai-reverse-${p.id}`}><Ban className="h-4 w-4" /></Button>}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}</TableBody>
                  </Table>
                </div>
              )}
            </SectionCard>
          </div>
        )}
        {addPay && data && <AddPaymentDialog detail={data} onClose={() => setAddPay(false)} onDone={() => { setAddPay(false); refresh(); }} />}
        {reverseTarget && <ReverseDialog payment={reverseTarget} onClose={() => setReverseTarget(null)} onDone={() => { setReverseTarget(null); refresh(); }} />}
      </DialogContent>
    </Dialog>
  );
}

function AddPaymentDialog({ detail, onClose, onDone }) {
  const rows = detail.rows;
  const remaining = detail.summary.remaining;
  const dpRow = rows.find((r) => r.kind === "dp");
  const dpOpen = dpRow && dpRow.remaining > 0;
  const instOpen = rows.some((r) => r.kind === "installment" && r.remaining > 0);
  const [type, setType] = useState(dpOpen ? "dp" : "installment");
  const [method, setMethod] = useState("transfer");
  const [f, setF] = useState({ received_at: todayWIB(), dest_account: "", reference: "", note: "" });
  const [alloc, setAlloc] = useState({});
  const [proof, setProof] = useState(null);
  const [loading, setLoading] = useState(false);
  const [warn, setWarn] = useState(false);
  const [idem] = useState(() => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random())));

  const targetRows = type === "dp" ? (dpRow && dpRow.remaining > 0 ? [dpRow] : [])
    : type === "installment" ? rows.filter((r) => r.kind === "installment" && r.remaining > 0)
    : rows.filter((r) => r.remaining > 0);
  const amount = type === "settlement" ? remaining
    : targetRows.reduce((sum, r) => sum + (Number(alloc[r.id] || 0)), 0);
  const allocations = type === "settlement"
    ? targetRows.map((r) => ({ installment_id: r.id, amount: r.remaining }))
    : targetRows.filter((r) => Number(alloc[r.id] || 0) > 0).map((r) => ({ installment_id: r.id, amount: Number(alloc[r.id]) }));

  const submit = async (confirmSimilar) => {
    if (amount <= 0) { toast.error("Nominal harus lebih besar dari nol."); return; }
    setLoading(true);
    try {
      let proofData = {};
      if (proof) { const r = await readFile(proof); proofData = { proof_base64: r.base64, proof_mime: r.mime }; }
      const res = await api.post("/angsuran/payments", {
        purchase_id: detail.purchase.id, payment_type: type, received_at: f.received_at, amount, method,
        dest_account: method === "transfer" ? f.dest_account : "", reference: f.reference, note: f.note,
        allocations: type === "settlement" ? undefined : allocations, ...proofData,
        idempotency_key: idem, confirm_similar: confirmSimilar,
      });
      if (res.data && res.data.needs_confirm) { setWarn(res.data.message || "Pembayaran serupa ditemukan."); setLoading(false); return; }
      toast.success(res.data.lunas ? "Pembayaran dibukukan. Transaksi LUNAS." : "Pembayaran berhasil dibukukan.");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto" data-testid="ai-payment-dialog">
        <DialogHeader><DialogTitle>Tambah Pembayaran</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Jenis Pembayaran</Label>
            <div className="mt-1 flex gap-2">
              {[["dp", "DP", dpOpen], ["installment", "Angsuran", instOpen], ["settlement", "Pelunasan", remaining > 0]].map(([v, l, en]) => (
                <button key={v} disabled={!en} onClick={() => setType(v)} data-testid={`ai-paytype-${v}`}
                  className={`rounded-full border px-3 py-1.5 text-sm ${type === v ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"} ${!en ? "cursor-not-allowed opacity-40" : ""}`}>{l}</button>
              ))}
            </div>
          </div>

          {type === "settlement" ? (
            <div className="rounded-lg border border-dashed p-3 text-sm" data-testid="ai-settlement-info">
              Pelunasan akan mengambil seluruh sisa kewajiban: <b>{formatRupiah(remaining)}</b>, dialokasikan otomatis ke {targetRows.length} tagihan tersisa.
            </div>
          ) : (
            <div className="rounded-lg border p-3">
              <div className="mb-2 text-xs font-medium text-muted-foreground">Alokasi ke tagihan (isi nominal per baris)</div>
              {targetRows.length === 0 ? <p className="text-sm text-muted-foreground">Tidak ada tagihan {type === "dp" ? "DP" : "angsuran"} yang tersisa.</p> : targetRows.map((r) => (
                <div key={r.id} className="mb-2 flex items-center gap-2">
                  <div className="flex-1 text-sm">{r.label} <span className="text-xs text-muted-foreground">(sisa {formatRupiah(r.remaining)})</span></div>
                  <Input type="number" className="w-40" placeholder="0" value={alloc[r.id] || ""} onChange={(e) => setAlloc((s) => ({ ...s, [r.id]: e.target.value }))} data-testid={`ai-alloc-${r.id}`} />
                </div>
              ))}
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label>Tanggal Diterima</Label><Input type="date" max={todayWIB()} value={f.received_at} onChange={(e) => setF((s) => ({ ...s, received_at: e.target.value }))} data-testid="ai-pay-date" /></div>
            <div><Label>Metode</Label>
              <Select value={method} onValueChange={setMethod}><SelectTrigger data-testid="ai-pay-method"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="transfer">Transfer</SelectItem><SelectItem value="cash">Tunai</SelectItem></SelectContent></Select></div>
            {method === "transfer" && <div><Label>Rekening Tujuan</Label><Input value={f.dest_account} onChange={(e) => setF((s) => ({ ...s, dest_account: e.target.value }))} data-testid="ai-pay-dest" /></div>}
            <div><Label>No. Referensi (opsional)</Label><Input value={f.reference} onChange={(e) => setF((s) => ({ ...s, reference: e.target.value }))} data-testid="ai-pay-ref" /></div>
            <div className="sm:col-span-2"><Label>Bukti Pembayaran (opsional, JPG/PNG/PDF)</Label><Input type="file" accept="image/*,application/pdf" onChange={(e) => setProof(e.target.files?.[0] || null)} data-testid="ai-pay-proof" /></div>
            <div className="sm:col-span-2"><Label>Keterangan</Label><Textarea value={f.note} onChange={(e) => setF((s) => ({ ...s, note: e.target.value }))} data-testid="ai-pay-note" /></div>
          </div>

          <div className="rounded-lg bg-muted p-3 text-sm" data-testid="ai-pay-summary">
            Ringkasan: nominal <b>{formatRupiah(amount)}</b> ke {allocations.length} tagihan.
            {amount > remaining && <span className="ml-2 text-red-600">Melebihi sisa kewajiban ({formatRupiah(remaining)}).</span>}
          </div>
          {warn && <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="ai-pay-warn">{warn} <div className="mt-2"><Button size="sm" onClick={() => submit(true)} disabled={loading} data-testid="ai-pay-confirm-similar">Ya, ini pembayaran berbeda — lanjutkan</Button></div></div>}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={() => submit(false)} disabled={loading || amount <= 0 || amount > remaining} data-testid="ai-pay-save">Simpan Pembayaran</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReverseDialog({ payment, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const save = async () => {
    if (!reason.trim()) { toast.error("Alasan wajib diisi."); return; }
    setLoading(true);
    try { await api.post(`/angsuran/payments/${payment.id}/reverse`, { reason }); toast.success("Pembayaran dibatalkan (reversal)."); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ai-reverse-dialog">
        <DialogHeader><DialogTitle>Batalkan Pembayaran (Reversal)</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Pembayaran {payment.receipt_number} sebesar {formatRupiah(payment.amount)} akan dibatalkan. Saldo dihitung ulang dan struk ditandai DIBATALKAN. Tindakan ini tidak dapat diulang.</p>
        <div><Label>Alasan Pembatalan</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="ai-reverse-reason" /></div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button variant="destructive" onClick={save} disabled={loading} data-testid="ai-reverse-save">Batalkan Pembayaran</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ----------------------------- Units ----------------------------- */
function UnitTab() {
  const qc = useQueryClient();
  const { projectId, projectParam, projects } = useProjects();
  const [edit, setEdit] = useState(null);
  const { data: units = [], isLoading } = useQuery({ queryKey: ["ai-units", projectId], queryFn: async () => (await api.get("/angsuran/units", { params: projectParam })).data });
  const del = async (u) => { if (!window.confirm(`Hapus unit ${u.code}?`)) return; try { await api.delete(`/angsuran/units/${u.id}`); toast.success("Unit dihapus."); qc.invalidateQueries({ queryKey: ["ai-units"] }); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <SectionCard title="Unit / Blok" description="Daftar unit yang dapat dibeli investor, per proyek."
      actions={<Button onClick={() => setEdit({})} data-testid="ai-add-unit"><Plus className="mr-2 h-4 w-4" />Tambah Unit</Button>} className="!p-0 overflow-hidden">
      {isLoading ? <Loading /> : units.length === 0 ? <div className="p-6"><EmptyState title="Belum ada unit" description="Tambahkan blok/unit yang dapat dibeli." action={<Button onClick={() => setEdit({})} data-testid="ai-empty-add-unit"><Plus className="mr-2 h-4 w-4" />Tambah Unit</Button>} /></div> : (
        <div className="overflow-x-auto"><Table>
          <TableHeader><TableRow><TableHead>Kode</TableHead><TableHead>Nama</TableHead><TableHead>Catatan</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>{units.map((u) => (
            <TableRow key={u.id} data-testid={`ai-unit-row-${u.id}`}>
              <TableCell className="font-medium">{u.code}</TableCell><TableCell>{u.name || "-"}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{u.note || "-"}</TableCell>
              <TableCell>{u.sold ? <Badge variant="secondary">Terjual</Badge> : <Badge className="bg-emerald-600">Tersedia</Badge>}</TableCell>
              <TableCell className="text-right"><div className="flex justify-end gap-1">
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEdit(u)} data-testid={`ai-unit-edit-${u.id}`}><Pencil className="h-4 w-4" /></Button>
                {!u.sold && <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => del(u)} data-testid={`ai-unit-del-${u.id}`}><Trash2 className="h-4 w-4" /></Button>}
              </div></TableCell>
            </TableRow>
          ))}</TableBody>
        </Table></div>
      )}
      {edit && <UnitDialog unit={edit} projects={projects} defaultProject={projectId !== "all" ? projectId : ""} onClose={() => setEdit(null)} onDone={() => { setEdit(null); qc.invalidateQueries({ queryKey: ["ai-units"] }); }} />}
    </SectionCard>
  );
}

function UnitDialog({ unit, projects, defaultProject, onClose, onDone }) {
  const isEdit = !!unit.id;
  const [f, setF] = useState({ project_id: unit.project_id || defaultProject || "", code: unit.code || "", name: unit.name || "", note: unit.note || "" });
  const [loading, setLoading] = useState(false);
  const save = async () => {
    if (!isEdit && !f.project_id) { toast.error("Pilih proyek."); return; }
    if (!f.code.trim()) { toast.error("Kode unit wajib."); return; }
    setLoading(true);
    try {
      if (isEdit) await api.patch(`/angsuran/units/${unit.id}`, { code: f.code, name: f.name, note: f.note });
      else await api.post("/angsuran/units", f);
      toast.success(isEdit ? "Unit diperbarui." : "Unit ditambahkan."); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ai-unit-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Ubah Unit" : "Tambah Unit"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!isEdit && <div><Label>Proyek</Label><Select value={f.project_id} onValueChange={(v) => setF((s) => ({ ...s, project_id: v }))}><SelectTrigger data-testid="ai-unit-project"><SelectValue placeholder="Pilih proyek" /></SelectTrigger><SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>}
          <div><Label>Kode Blok/Unit</Label><Input value={f.code} onChange={(e) => setF((s) => ({ ...s, code: e.target.value }))} data-testid="ai-unit-code" /></div>
          <div><Label>Nama (opsional)</Label><Input value={f.name} onChange={(e) => setF((s) => ({ ...s, name: e.target.value }))} data-testid="ai-unit-name" /></div>
          <div><Label>Catatan (opsional)</Label><Textarea value={f.note} onChange={(e) => setF((s) => ({ ...s, note: e.target.value }))} data-testid="ai-unit-note" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={loading} data-testid="ai-unit-save">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ----------------------------- Investors (buyers) ----------------------------- */
function InvestorTab() {
  const qc = useQueryClient();
  const { isSuperadmin } = useAuth();
  const [edit, setEdit] = useState(null);
  const { data: buyers = [], isLoading } = useQuery({ queryKey: ["ai-buyers"], queryFn: async () => (await api.get("/angsuran/buyers")).data });
  const viewKtp = async (b) => { try { const res = await api.get(`/angsuran/buyers/${b.id}/ktp`, { responseType: "blob" }); window.open(URL.createObjectURL(res.data), "_blank"); } catch { toast.error("Lampiran KTP tidak tersedia."); } };
  return (
    <SectionCard title="Investor / Pembeli" description="Data KYC pembeli unit. NIK & KTP dibatasi sesuai hak akses."
      actions={<Button onClick={() => setEdit({})} data-testid="ai-add-buyer"><Plus className="mr-2 h-4 w-4" />Tambah Investor</Button>} className="!p-0 overflow-hidden">
      {isLoading ? <Loading /> : buyers.length === 0 ? <div className="p-6"><EmptyState title="Belum ada investor" description="Tambahkan data investor/pembeli." action={<Button onClick={() => setEdit({})} data-testid="ai-empty-add-buyer"><Plus className="mr-2 h-4 w-4" />Tambah Investor</Button>} /></div> : (
        <div className="overflow-x-auto"><Table>
          <TableHeader><TableRow><TableHead>Nama (KTP)</TableHead><TableHead>NIK</TableHead><TableHead>Telepon</TableHead><TableHead>Alamat</TableHead><TableHead>KTP</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>{buyers.map((b) => (
            <TableRow key={b.id} data-testid={`ai-buyer-row-${b.id}`}>
              <TableCell className="font-medium">{b.name}</TableCell>
              <TableCell className="tabular-nums text-xs">{b.nik}</TableCell>
              <TableCell className="text-xs">{b.phone || "-"}</TableCell>
              <TableCell className="max-w-[220px] truncate text-xs text-muted-foreground">{b.address_ktp || "-"}</TableCell>
              <TableCell>{b.has_ktp ? (isSuperadmin ? <Button size="sm" variant="outline" onClick={() => viewKtp(b)} data-testid={`ai-buyer-ktp-${b.id}`}><Eye className="mr-1 h-3.5 w-3.5" />Lihat</Button> : <span className="text-xs text-muted-foreground">Ada (terbatas)</span>) : <span className="text-xs text-muted-foreground">-</span>}</TableCell>
              <TableCell className="text-right"><Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEdit(b)} data-testid={`ai-buyer-edit-${b.id}`}><Pencil className="h-4 w-4" /></Button></TableCell>
            </TableRow>
          ))}</TableBody>
        </Table></div>
      )}
      {edit && <BuyerDialog buyer={edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); qc.invalidateQueries({ queryKey: ["ai-buyers"] }); }} />}
    </SectionCard>
  );
}

function BuyerDialog({ buyer, onClose, onDone }) {
  const isEdit = !!buyer.id;
  const [f, setF] = useState({ name: buyer.name || "", nik: isEdit ? "" : "", address_ktp: buyer.address_ktp || "", phone: buyer.phone || "", note: buyer.note || "" });
  const [ktp, setKtp] = useState(null);
  const [loading, setLoading] = useState(false);
  const save = async () => {
    if (!f.name.trim()) { toast.error("Nama sesuai KTP wajib."); return; }
    if ((!isEdit || f.nik) && !/^\d{16}$/.test(f.nik)) { toast.error("NIK harus 16 digit angka."); return; }
    setLoading(true);
    try {
      let ktpData = {};
      if (ktp) { const r = await readFile(ktp); ktpData = { ktp_base64: r.base64, ktp_mime: r.mime }; }
      const payload = { name: f.name, address_ktp: f.address_ktp, phone: f.phone, note: f.note, ...ktpData };
      if (f.nik) payload.nik = f.nik;
      if (isEdit) await api.patch(`/angsuran/buyers/${buyer.id}`, payload);
      else await api.post("/angsuran/buyers", payload);
      toast.success(isEdit ? "Investor diperbarui." : "Investor ditambahkan."); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto" data-testid="ai-buyer-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Ubah Investor" : "Tambah Investor"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nama Lengkap sesuai KTP</Label><Input value={f.name} onChange={(e) => setF((s) => ({ ...s, name: e.target.value }))} data-testid="ai-buyer-name" /></div>
          <div><Label>NIK (16 digit){isEdit && <span className="ml-1 text-xs text-muted-foreground">— kosongkan jika tidak diubah</span>}</Label><Input value={f.nik} onChange={(e) => setF((s) => ({ ...s, nik: e.target.value }))} placeholder={isEdit ? "••••••••••••" : ""} data-testid="ai-buyer-nik" /></div>
          <div><Label>Alamat sesuai KTP</Label><Textarea value={f.address_ktp} onChange={(e) => setF((s) => ({ ...s, address_ktp: e.target.value }))} data-testid="ai-buyer-address" /></div>
          <div><Label>Nomor Telepon</Label><Input value={f.phone} onChange={(e) => setF((s) => ({ ...s, phone: e.target.value }))} data-testid="ai-buyer-phone" /></div>
          <div><Label>Lampiran KTP (JPG/PNG/PDF, maks 5MB)</Label><Input type="file" accept="image/*,application/pdf" onChange={(e) => setKtp(e.target.files?.[0] || null)} data-testid="ai-buyer-ktp-file" /></div>
          <div><Label>Catatan</Label><Textarea value={f.note} onChange={(e) => setF((s) => ({ ...s, note: e.target.value }))} data-testid="ai-buyer-note" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={loading} data-testid="ai-buyer-save">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ----------------------------- Report ----------------------------- */
function LaporanTab() {
  const { projectId, projectParam } = useProjects();
  const [f, setF] = useState({ from: "", to: "", type: "all" });
  const params = { ...projectParam, date_from: f.from || undefined, date_to: f.to || undefined, type: f.type === "all" ? undefined : f.type };
  const { data, isLoading } = useQuery({ queryKey: ["ai-report", projectId, f], queryFn: async () => (await api.get("/angsuran/report", { params })).data });
  const exportFile = async (kind) => {
    try { const res = await api.get(`/angsuran/report/export.${kind}`, { params, responseType: "blob" });
      if (kind === "pdf") window.open(URL.createObjectURL(res.data), "_blank"); else downloadBlob(res.data, "laporan-angsuran-investor.xlsx");
    } catch { toast.error("Gagal mengekspor."); }
  };
  const s = data?.summary;
  return (
    <div className="space-y-4">
      <SectionCard title="Laporan Penerimaan Pembayaran Investor" description="Hanya menghitung uang yang sudah diterima. Koreksi/reversal tampil negatif pada tanggal koreksi.">
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-xs">Dari</Label><Input type="date" value={f.from} onChange={(e) => setF((x) => ({ ...x, from: e.target.value }))} data-testid="ai-rep-from" /></div>
          <div><Label className="text-xs">Sampai</Label><Input type="date" value={f.to} onChange={(e) => setF((x) => ({ ...x, to: e.target.value }))} data-testid="ai-rep-to" /></div>
          <div><Label className="text-xs">Jenis</Label><Select value={f.type} onValueChange={(v) => setF((x) => ({ ...x, type: v }))}><SelectTrigger className="w-[150px]" data-testid="ai-rep-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Semua</SelectItem><SelectItem value="dp">DP</SelectItem><SelectItem value="installment">Angsuran</SelectItem><SelectItem value="settlement">Pelunasan</SelectItem></SelectContent></Select></div>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" onClick={() => exportFile("xlsx")} data-testid="ai-rep-xlsx"><Download className="mr-2 h-4 w-4" />Excel</Button>
            <Button variant="outline" onClick={() => exportFile("pdf")} data-testid="ai-rep-pdf"><FileText className="mr-2 h-4 w-4" />PDF</Button>
          </div>
        </div>
      </SectionCard>
      {isLoading || !s ? <Loading /> : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
            <StatCard label="DP" value={<Money value={s.dp} />} testid="ai-rep-s-dp" />
            <StatCard label="Angsuran" value={<Money value={s.installment} />} />
            <StatCard label="Pelunasan" value={<Money value={s.settlement} />} />
            <StatCard label="Bruto" value={<Money value={s.gross} />} accent="#0369a1" />
            <StatCard label="Koreksi" value={<span className="text-red-600">-{formatRupiah(s.corrections)}</span>} accent="#b91c1c" />
            <StatCard label="Bersih" value={<Money value={s.net} />} accent="#047857" testid="ai-rep-s-net" />
          </div>
          <SectionCard className="!p-0 overflow-hidden" testid="ai-rep-table">
            {data.rows.length === 0 ? <div className="p-6"><EmptyState title="Tidak ada penerimaan pada periode ini" /></div> : (
              <div className="overflow-x-auto"><Table>
                <TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>No. Struk/Ref</TableHead><TableHead>Investor</TableHead><TableHead>Unit</TableHead><TableHead>Jenis</TableHead><TableHead className="text-right">Nominal</TableHead><TableHead>Pencatat</TableHead></TableRow></TableHeader>
                <TableBody>{data.rows.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs">{formatTanggal(r.date)}</TableCell>
                    <TableCell className="text-xs">{r.ref}</TableCell>
                    <TableCell>{r.buyer}</TableCell>
                    <TableCell className="text-xs">{r.unit}</TableCell>
                    <TableCell>{r.type_label}</TableCell>
                    <TableCell className={`text-right tabular-nums ${r.amount < 0 ? "text-red-600" : ""}`}>{r.amount < 0 ? "-" : ""}{formatRupiah(Math.abs(r.amount))}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.recorded_by}</TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table></div>
            )}
          </SectionCard>
        </>
      )}
    </div>
  );
}
